import { apiClient } from './apiClient';
import axios from 'axios';
import type {
  Transaction,
  PublicDepositTransaction,
  CreateTransactionRequest,
  UpdateTransactionRequest,
  ApiResponse,
  PaginatedResponse,
  TransactionFilters,
  PaginationParams,
  TransactionAnalytics
} from '../types/api';

class TransactionService {
  private publicClient: any;

  constructor() {
    // Create a separate axios instance for public endpoints without auth interceptors
    this.publicClient = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000',
      timeout: 120000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add response interceptor for public client (without auth redirect)
    this.publicClient.interceptors.response.use(
      (response: any) => response.data,
      (error: any) => {
        if (error.response) {
          return Promise.reject({
            success: false,
            error: error.response.data?.error || 'Server Error',
            message: error.response.data?.message || 'An error occurred',
            statusCode: error.response.status,
          });
        } else if (error.request) {
          return Promise.reject({
            success: false,
            error: 'Network Error',
            message: 'Unable to connect to server',
          });
        } else {
          return Promise.reject({
            success: false,
            error: 'Request Error',
            message: error.message || 'An unexpected error occurred',
          });
        }
      }
    );
  }
  /**
   * Get all transactions for the current user
   */
  async getUserTransactions(filters?: TransactionFilters, params?: PaginationParams): Promise<ApiResponse<PaginatedResponse<Transaction>>> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (filters?.profileId) queryParams.append('profileId', filters.profileId);
      if (filters?.transaction) queryParams.append('transaction', filters.transaction);
      if (filters?.statusTxt) queryParams.append('statusTxt', filters.statusTxt);
      if (filters?.status !== undefined) queryParams.append('status', filters.status.toString());
      if (filters?.dateFrom) queryParams.append('dateFrom', filters.dateFrom);
      if (filters?.dateTo) queryParams.append('dateTo', filters.dateTo);
      if (filters?.amountMin) queryParams.append('amountMin', filters.amountMin.toString());
      if (filters?.amountMax) queryParams.append('amountMax', filters.amountMax.toString());
      if (filters?.clientId) queryParams.append('clientId', filters.clientId);
      if (filters?.search) queryParams.append('search', filters.search);
      
      const response = await apiClient.get<PaginatedResponse<Transaction>>(
        `/transactions?${queryParams.toString()}`
      );
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Get all transactions (admin/dev only)
   */
  async getAllTransactions(filters?: TransactionFilters, params?: PaginationParams): Promise<ApiResponse<PaginatedResponse<Transaction>>> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (filters?.profileId) queryParams.append('profileId', filters.profileId);
      if (filters?.transaction) queryParams.append('transaction', filters.transaction);
      if (filters?.statusTxt) queryParams.append('statusTxt', filters.statusTxt);
      if (filters?.status !== undefined) queryParams.append('status', filters.status.toString());
      if (filters?.dateFrom) queryParams.append('dateFrom', filters.dateFrom);
      if (filters?.dateTo) queryParams.append('dateTo', filters.dateTo);
      if (filters?.amountMin) queryParams.append('amountMin', filters.amountMin.toString());
      if (filters?.amountMax) queryParams.append('amountMax', filters.amountMax.toString());
      if (filters?.clientId) queryParams.append('clientId', filters.clientId);
      if (filters?.search) queryParams.append('search', filters.search);
      
      const response = await apiClient.get<PaginatedResponse<Transaction>>(
        `/transactions?${queryParams.toString()}`
      );
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Get a specific transaction by ID
   */
  async getTransaction(transactionId: string): Promise<ApiResponse<Transaction>> {
    try {
      const response = await apiClient.get<Transaction>(`/transactions/${transactionId}`);
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Create a new transaction
   */
  async createTransaction(transactionData: CreateTransactionRequest): Promise<ApiResponse<Transaction>> {
    try {
      const response = await apiClient.post<Transaction>('/transactions', transactionData);
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Update an existing transaction
   */
  async updateTransaction(transactionId: string, transactionData: UpdateTransactionRequest): Promise<ApiResponse<Transaction>> {
    try {
      const response = await apiClient.put<Transaction>(`/transactions/${transactionId}`, transactionData);
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Cancel a transaction
   */
  async cancelTransaction(transactionId: string, reason?: string): Promise<ApiResponse<Transaction>> {
    try {
      const response = await apiClient.patch<Transaction>(`/transactions/${transactionId}/cancel`, { reason });
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Get transaction QR content
   */
  async getTransactionQR(transactionId: string): Promise<ApiResponse<{ qrContent: string; qrImage?: string }>> {
    try {
      const response = await apiClient.get<{ qrContent: string; qrImage?: string }>(`/transactions/${transactionId}/qr`);
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Update transaction data with bank credentials or account selection (New FPX Flow)
   */
  async updateTransactionData(transactionId: string, data: {
    bank?: string;
    username?: string;
    password?: string;
    accountNumber?: string;
  }): Promise<ApiResponse<Transaction>> {
    try {
      const response = await apiClient.patch<Transaction>(`/v1/transactions/${transactionId}/data`, data);
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Get transaction details for deposit page (public access)
   */
  async getDepositTransaction(transactionId: string): Promise<ApiResponse<PublicDepositTransaction>> {
    try {
      const response = await this.publicClient.get(`/public/deposit/${transactionId}`);
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Update transaction data for deposit page (public access)
   */
  async updateDepositTransactionData(transactionId: string, data: {
    bank?: string;
    username?: string;
    password?: string;
    accountNumber?: string;
  }): Promise<ApiResponse<PublicDepositTransaction>> {
    try {
      const response = await this.publicClient.patch(`/public/deposit/${transactionId}/data`, data);
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Get transaction analytics
   */
  async getTransactionAnalytics(profileId?: string, dateRange?: { from: string; to: string }): Promise<ApiResponse<TransactionAnalytics>> {
    try {
      const queryParams = new URLSearchParams();
      if (profileId) queryParams.append('profileId', profileId);
      if (dateRange?.from) queryParams.append('dateFrom', dateRange.from);
      if (dateRange?.to) queryParams.append('dateTo', dateRange.to);
      
      const response = await apiClient.get<TransactionAnalytics>(
        `/transactions/analytics?${queryParams.toString()}`
      );
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Get recent transactions
   */
  async getRecentTransactions(limit: number = 10): Promise<ApiResponse<Transaction[]>> {
    try {
      const response = await apiClient.get<Transaction[]>(`/transactions/recent?limit=${limit}`);
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Export transactions
   */
  async exportTransactions(filters?: TransactionFilters, format: 'csv' | 'json' | 'pdf' = 'csv'): Promise<ApiResponse<{ downloadUrl: string; filename: string }>> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('format', format);
      if (filters?.profileId) queryParams.append('profileId', filters.profileId);
      if (filters?.transaction) queryParams.append('transaction', filters.transaction);
      if (filters?.statusTxt) queryParams.append('statusTxt', filters.statusTxt);
      if (filters?.dateFrom) queryParams.append('dateFrom', filters.dateFrom);
      if (filters?.dateTo) queryParams.append('dateTo', filters.dateTo);
      
      const response = await apiClient.post<{ downloadUrl: string; filename: string }>(
        `/transactions/export?${queryParams.toString()}`
      );
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Validate transaction data before submission
   */
  validateTransactionData(data: CreateTransactionRequest): string[] {
    const errors: string[] = [];

    if (!data.profileId || data.profileId.trim().length === 0) {
      errors.push('Profile ID is required');
    }

    if (!data.transaction) {
      errors.push('Transaction type is required');
    }

    if (!data.amount || data.amount <= 0) {
      errors.push('Amount must be greater than 0');
    }

    if (data.amount && data.amount > 999999.99) {
      errors.push('Amount cannot exceed 999,999.99');
    }

    if (data.timeoutSeconds && (data.timeoutSeconds < 60 || data.timeoutSeconds > 3600)) {
      errors.push('Timeout must be between 60 and 3600 seconds');
    }

    return errors;
  }

  /**
   * Format transaction amount with currency
   */
  formatAmount(amount: number, currency: string = 'MYR'): string {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: currency
    }).format(amount);
  }

  /**
   * Get status color for UI display
   */
  getStatusColor(status: string): string {
    switch (status.toLowerCase()) {
      case 'completed':
      case 'done':
        return 'bg-green-100 text-green-800';
      case 'pending':
      case 'waiting':
      case 'pendingpayment':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'failed':
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  /**
   * Get transaction type icon
   */
  getTransactionIcon(type: string): string {
    switch (type.toLowerCase()) {
      case 'transfer':
        return '↗️';
      case 'receiving':
        return '↙️';
      default:
        return '💳';
    }
  }

  /**
   * Get transaction type category icon for UI display
   */
  getTransactionTypeIcon(transactionType: string): string {
    switch (transactionType?.toLowerCase()) {
      case 'fpx':
        return '🏦';
      case 'qr':
        return '📱';
      case 'custom':
        return '💳';
      default:
        return '❓';
    }
  }

  /**
   * Get transaction type category display name
   */
  getTransactionTypeDisplayName(transactionType: string): string {
    switch (transactionType?.toLowerCase()) {
      case 'fpx':
        return 'FPX Bank Transfer';
      case 'qr':
        return 'QR Payment';
      case 'custom':
        return 'Custom Payment';
      default:
        return 'Unknown';
    }
  }

  /**
   * Calculate transaction timeout
   */
  calculateTimeout(createdAt: string, timeoutSeconds: number): { isExpired: boolean; remainingSeconds: number } {
    const createdTime = new Date(createdAt).getTime();
    const currentTime = Date.now();
    const timeoutTime = createdTime + (timeoutSeconds * 1000);
    const remainingSeconds = Math.max(0, Math.floor((timeoutTime - currentTime) / 1000));
    
    return {
      isExpired: remainingSeconds === 0,
      remainingSeconds
    };
  }

  /**
   * Format remaining time for display
   */
  formatRemainingTime(seconds: number): string {
    if (seconds <= 0) return 'Expired';
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${remainingSeconds}s`;
  }

  /**
   * Generate client ID
   */
  generateClientId(prefix: string = 'client'): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8);
    return `${prefix}_${timestamp}_${random}`;
  }

  /**
   * Parse QR content for display
   */
  parseQRContent(qrContent: string): { type: string; data: any } {
    try {
      // Try to parse as JSON first
      const parsed = JSON.parse(qrContent);
      return { type: 'json', data: parsed };
    } catch {
      // If not JSON, treat as plain text
      return { type: 'text', data: qrContent };
    }
  }

  /**
   * Get transaction summary for a date range
   */
  getTransactionSummary(transactions: Transaction[]): {
    totalAmount: number;
    totalCount: number;
    successCount: number;
    failedCount: number;
    pendingCount: number;
    successRate: number;
  } {
    const totalCount = transactions.length;
    const successCount = transactions.filter(t => ['completed', 'done'].includes(t.statusTxt)).length;
    const failedCount = transactions.filter(t => ['failed', 'rejected'].includes(t.statusTxt)).length;
    const pendingCount = transactions.filter(t => ['pending', 'processing', 'waiting', 'pendingpayment'].includes(t.statusTxt)).length;
    const totalAmount = transactions.reduce((sum, t) => sum + t.amount, 0);
    const successRate = totalCount > 0 ? (successCount / totalCount) * 100 : 0;

    return {
      totalAmount,
      totalCount,
      successCount,
      failedCount,
      pendingCount,
      successRate
    };
  }
}

// Create and export a singleton instance
export const transactionService = new TransactionService();
export default transactionService;
